import { AppointmentPractitioner } from '@dreryk/egabinet-appointment-frontend-api';
import { PatientSimple } from '@dreryk/egabinet-patient-frontend-api';

import { TimetableSlotSelectionEvent } from '@dreryk/react-calendar';
import {
   Modal,
   ModalCancelButton,
   ModalCloseButton,
   ModalMaxHeight,
   ModalWidth,
   TestableComponentProps,
   ValidateFormEventInterceptor,
} from '@dreryk/react-components';

import { VideoConsultationPatientContactUpdateShowModalEventInterceptor } from '~libs.desktop.video-consultation';

import { appointmentsTranslationKeys } from '../../../../translations';

import { AppointmentCreateForm, AppointmentCreateFormController } from './AppointmentCreateForm';
import { AppointmentCreateFormLoader } from './AppointmentCreateForm.loader';
import { appointmentCreateFormInitializer, modalConfirmExitConfig } from './AppointmentCreateModal.utils';

type AppointmentCreateModalConfirmButtonRenderer = (
   renderProps: Pick<AppointmentCreateFormController, 'handleSubmit'> & {
      setAppointmentTimeSlotOccupiedError: VoidFunction;
   }
) => JSX.Element;

export type AppointmentCreateModalProps = TestableComponentProps & {
   readonly selectedSlot: TimetableSlotSelectionEvent<AppointmentPractitioner>;
   readonly overlappingAppointmentsAllowed: boolean;
   readonly onClose: VoidFunction;
   readonly confirmButton: AppointmentCreateModalConfirmButtonRenderer;
   readonly selectedPatient?: PatientSimple;
};

export const AppointmentCreateModal = (props: AppointmentCreateModalProps): JSX.Element => {
   return (
      <Modal
         testId={props.testId}
         title={appointmentsTranslationKeys.create.modal.title.register}
         width={ModalWidth.WIDE}
         height={ModalMaxHeight.TALL}
      >
         <AppointmentCreateFormLoader selectedSlot={props.selectedSlot}>
            {({
               practitioners,
               healthcareProviderLocations,
               healthcareProviderLocationIdentifierFromSchedule,
               onlinePaymentsIntegrationExists,
            }) => (
               <AppointmentCreateForm
                  overlappingAppointmentsAllowed={props.overlappingAppointmentsAllowed}
                  initializer={appointmentCreateFormInitializer({
                     selectedSlot: props.selectedSlot,
                     healthcareLocationProviders: healthcareProviderLocations,
                     healthcareProviderLocationIdentifierFromSchedule: healthcareProviderLocationIdentifierFromSchedule,
                     patient: props.selectedPatient,
                  })}
                  practitioners={practitioners}
                  healthcareProviderLocations={healthcareProviderLocations}
                  patientCreateAllowed={!props.selectedPatient}
                  onlinePaymentsIntegrationExists={onlinePaymentsIntegrationExists}
               >
                  {({ handleSubmit, formState, setAppointmentTimeSlotOccupiedError, trigger, getValue }) => {
                     const patientId = getValue('patient')?.id ?? null;
                     const videoConsultationEnabled = Boolean(getValue('videoConsultation'));
                     return (
                        <>
                           <ValidateFormEventInterceptor trigger={trigger} silent>
                              <VideoConsultationPatientContactUpdateShowModalEventInterceptor
                                 patientId={patientId}
                                 shouldShowModal={videoConsultationEnabled}
                              >
                                 {props.confirmButton({
                                    handleSubmit: handleSubmit,
                                    setAppointmentTimeSlotOccupiedError: setAppointmentTimeSlotOccupiedError,
                                 })}
                              </VideoConsultationPatientContactUpdateShowModalEventInterceptor>
                           </ValidateFormEventInterceptor>

                           <ModalCancelButton
                              onClick={props.onClose}
                              shouldConfirmExit={modalConfirmExitConfig(formState.isDirty)}
                           />
                           <ModalCloseButton
                              onClick={props.onClose}
                              shouldConfirmExit={modalConfirmExitConfig(formState.isDirty)}
                           />
                        </>
                     );
                  }}
               </AppointmentCreateForm>
            )}
         </AppointmentCreateFormLoader>
      </Modal>
   );
};
